import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ShieldCheck, RefreshCw, Globe, Monitor, User, Crown, Trash2, Eraser } from "lucide-react";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "boe_admin_token";
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` });

const fmtDateTime = (iso) => {
  try {
    return new Date(iso).toLocaleString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

const SecurityPanel = ({ onLogout }) => {
  const [logs, setLogs] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [clearing, setClearing] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true);
    try {
      const { data } = await axios.get(`${API}/admin/login-logs?limit=300`, { headers: authHeader() });
      setLogs(data);
    } catch (err) {
      if (err.response?.status === 401) return onLogout();
      toast.error("Login-Protokoll konnte nicht geladen werden.");
      setLogs([]);
    } finally {
      setRefreshing(false);
    }
  }, [onLogout]);

  useEffect(() => { load(); }, [load]);

  const removeOne = async (id) => {
    setDeletingId(id);
    try {
      await axios.delete(`${API}/admin/login-logs/${id}`, { headers: authHeader() });
      setLogs((prev) => prev.filter((l) => l.id !== id));
      toast.success("Eintrag gelöscht.");
    } catch (err) {
      if (err.response?.status === 401) return onLogout();
      toast.error("Löschen fehlgeschlagen.");
    } finally {
      setDeletingId(null);
    }
  };

  const clearAll = async () => {
    setClearing(true);
    try {
      const { data } = await axios.delete(`${API}/admin/login-logs/clear`, { headers: authHeader() });
      setLogs([]);
      toast.success(`Verlauf geleert (${data.deleted_count} Einträge).`);
    } catch (err) {
      if (err.response?.status === 401) return onLogout();
      toast.error("Verlauf konnte nicht geleert werden.");
    } finally {
      setClearing(false);
    }
  };

  if (logs === null) return <div className="flex items-center justify-center py-24 text-cream/60"><Loader2 size={28} className="animate-spin" /></div>;

  const adminCount = logs.filter((l) => l.account_type === "admin").length;
  const portalCount = logs.filter((l) => l.account_type === "portal").length;

  return (
    <div data-testid="security-panel">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6 mb-10">
        <div>
          <span className="section-eyebrow">Sicherheit</span>
          <h1 className="mt-3 text-3xl md:text-4xl font-semibold text-cream tracking-tight">Login-Protokoll</h1>
          <p className="mt-2 text-sm text-cream/60">Jeder erfolgreiche Login mit Zeit, IP-Adresse, Land und Gerät.</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="rounded-2xl border border-gold/30 bg-white/[0.04] px-6 py-4 text-center" data-testid="security-stats">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: "#C69C22" }}>
              <ShieldCheck size={15} /> Logins
            </div>
            <p className="mt-1 text-3xl font-semibold text-cream">{logs.length}</p>
            <p className="mt-1 text-[11px] text-cream/50">{portalCount} Mitglieder · {adminCount} Admin</p>
          </div>
          <div className="flex flex-col gap-2">
            <button
              onClick={() => load(true)}
              disabled={refreshing}
              data-testid="security-refresh"
              className="inline-flex items-center gap-2 rounded-full border border-gold/40 px-4 py-2.5 text-sm font-semibold hover:bg-gold hover:text-anthracite transition-colors disabled:opacity-50"
              style={{ color: "#E5C76B" }}
            >
              {refreshing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />} Aktualisieren
            </button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button
                  disabled={logs.length === 0 || clearing}
                  data-testid="security-clear"
                  className="inline-flex items-center gap-2 rounded-full border border-red-500/40 px-4 py-2.5 text-sm font-semibold text-red-300 hover:bg-red-600 hover:text-white transition-colors disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-red-300"
                >
                  {clearing ? <Loader2 size={16} className="animate-spin" /> : <Eraser size={16} />} Verlauf leeren
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-anthracite-soft border border-gold/20 text-cream">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-cream">Gesamten Login-Verlauf leeren?</AlertDialogTitle>
                  <AlertDialogDescription className="text-cream/60">
                    Alle {logs.length} Protokoll-Einträge werden unwiderruflich gelöscht. Neue Logins werden weiterhin aufgezeichnet.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="bg-transparent border-white/20 text-cream hover:bg-white/10 hover:text-cream">Abbrechen</AlertDialogCancel>
                  <AlertDialogAction data-testid="security-clear-confirm" onClick={clearAll} className="bg-red-600 text-white hover:bg-red-700">Alles löschen</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>

      {logs.length === 0 ? (
        <p className="text-sm text-cream/45 py-12 text-center border border-dashed border-white/10 rounded-2xl" data-testid="security-empty">
          Noch keine Logins protokolliert.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-gold/15" data-testid="security-log-table">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.04] text-cream/55 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left font-semibold px-5 py-3">Zeitpunkt</th>
                <th className="text-left font-semibold px-5 py-3">Konto</th>
                <th className="text-left font-semibold px-5 py-3">Rang / Typ</th>
                <th className="text-left font-semibold px-5 py-3">IP-Adresse</th>
                <th className="text-left font-semibold px-5 py-3">Land</th>
                <th className="text-left font-semibold px-5 py-3">Gerät</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {logs.map((l) => {
                const isAdmin = l.account_type === "admin";
                const rank = l.user_rank || (isAdmin ? "Admin" : "Mitglied");
                return (
                  <tr key={l.id} data-testid={`security-log-${l.id}`} className="border-t border-white/8 hover:bg-white/[0.02]">
                    <td className="px-5 py-3 text-cream/75 whitespace-nowrap">{fmtDateTime(l.created_at)}</td>
                    <td className="px-5 py-3 font-semibold text-cream">
                      <span className="inline-flex items-center gap-1.5"><User size={13} className="text-cream/50" /> {l.user_name || "—"}</span>
                    </td>
                    <td className="px-5 py-3">
                      {isAdmin ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-gold/20 border border-gold/40 px-2.5 py-0.5 text-[10px] uppercase tracking-wider font-semibold" style={{ color: "#E5C76B" }}><Crown size={11} /> Admin</span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-white/8 border border-gold/25 px-2.5 py-0.5 text-[10px] uppercase tracking-wider font-semibold" style={{ color: "#E5C76B" }} data-testid={`security-log-rank-${l.id}`}>{rank}</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-cream/70 font-mono text-xs">{l.ip || "—"}</td>
                    <td className="px-5 py-3 text-cream/70">
                      <span className="inline-flex items-center gap-1.5"><Globe size={13} className="text-cream/45" /> {l.country || "Unbekannt"}</span>
                    </td>
                    <td className="px-5 py-3 text-cream/70">
                      <span className="inline-flex items-center gap-1.5"><Monitor size={13} className="text-cream/45" /> {l.device || "—"}</span>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => removeOne(l.id)} disabled={deletingId === l.id} data-testid={`security-delete-${l.id}`} aria-label="Eintrag löschen" title="Diesen Eintrag löschen" className="text-cream/40 hover:text-red-400 transition-colors disabled:opacity-50">
                        {deletingId === l.id ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SecurityPanel;
